import React from "react";
import { Link } from "react-router-dom";
import {
  Scan, Barcode, FolderKanban, BellRing, Clock, ArrowRight, CheckCircle2,
  ArrowDown, Smartphone, Mail, ShieldCheck,
} from "lucide-react";
import { ThemeToggle } from "../theme";
import { PfuLogo } from "../components/Logo";
import { usePricing } from "../usePricing";

const LOGO_WE = "/we-logo.png";

const STEPS = [
  { n: "01", title: "Automated daily PFU scan", desc: "Every morning (Mauritius time) we fetch the official PFU catalogue — Retail & Subsidised — and index it for you." },
  { n: "02", title: "Add products or import CSV", desc: "Track items by EAN code. Single-item entry with live validation, or bulk CSV upload with duplicate detection." },
  { n: "03", title: "Get instant price alerts", desc: "Browser push on iPhone & Android, plus email — the moment a tracked max retail price changes." },
];

const FEATURES = [
  { icon: Scan, title: "Daily Mauritius PFU scan", desc: "Covers official Maximum Retail Prices for Retail and Subsidised price-controlled goods." },
  { icon: Barcode, title: "EAN tracking & CSV import", desc: "Add items individually or upload a CSV with automatic validation and duplicate detection." },
  { icon: FolderKanban, title: "Up to 5 monitoring profiles", desc: "Organise tracked items into separate profiles — groceries, pharmacy, wholesale and more." },
  { icon: BellRing, title: "Push & email alerts", desc: "Native PWA push on mobile and desktop, plus email — with an optional daily digest." },
  { icon: Clock, title: "Run Now & 30-day history", desc: "Trigger an on-demand scan any time, and review itemised price diffs up to 30 days back." },
  { icon: ShieldCheck, title: "No-change confirmations", desc: "Even on quiet days we email a confirmation, so you always know the scan ran." },
];

const PLAN_FEATURES = [
  "Daily PFU catalogue scans (Mauritius time)",
  "Track products by EAN — manual or CSV",
  "Up to 5 monitoring profiles",
  "PWA push notifications (iOS & Android)",
  "Email alerts & daily digest",
  "Manual 'Run Now' & custom-date scans",
  "30-day scan history with price diffs",
];

function Nav() {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-stone-surface/80 border-b border-stone-border">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
          <PfuLogo size={32} />
          <span className="font-display font-bold text-lg text-ink">PFU Tracker</span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm text-muted">
          <a href="#how" className="hover:text-ink transition-colors">How it works</a>
          <a href="#features" className="hover:text-ink transition-colors">Features</a>
          <a href="#pricing" className="hover:text-ink transition-colors">Pricing</a>
        </nav>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Link to="/login" className="btn-ghost hidden sm:inline-flex" data-testid="landing-login-btn">Log in</Link>
          <Link to="/signup" className="btn-primary" data-testid="landing-signup-btn">Start free</Link>
        </div>
      </div>
    </header>
  );
}

function AlertMock() {
  return (
    <div className="card glow-border p-5 rise-in" style={{ animationDelay: "0.15s" }} data-testid="landing-alert-mock">
      <div className="flex items-center justify-between mb-4">
        <span className="chip glow-badge bg-teal-light text-teal font-mono text-[11px] uppercase tracking-wider">Just updated · 07:00 MUT</span>
        <BellRing className="w-4 h-4 text-teal" />
      </div>
      <div className="text-sm text-muted mb-1">Basmati Rice 5kg · Subsidised</div>
      <div className="font-mono text-xs text-muted mb-4">EAN 6091234567891</div>
      <div className="flex items-end gap-3 mb-4">
        <span className="text-muted line-through text-lg">Rs 175.00</span>
        <ArrowRight className="w-4 h-4 text-muted mb-1.5" />
        <span className="font-display text-2xl font-bold text-emerald glow-text">Rs 162.50</span>
        <span className="chip bg-emerald-light text-emerald mb-1">-7.14%</span>
      </div>
      <div className="flex items-center gap-3 text-xs text-muted pt-3 border-t border-stone-border">
        <span className="flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5 text-teal" /> Push sent</span>
        <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-teal" /> Email sent</span>
      </div>
    </div>
  );
}

export default function Landing() {
  const pricing = usePricing();

  return (
    <div className="min-h-full">
      <Nav />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 rise-in">
          <span className="chip bg-teal-light text-teal font-mono text-[11px] uppercase tracking-wider mb-5" data-testid="landing-hero-badge">
            Official Mauritius PFU catalogue · Automatic daily scans
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-ink">
            Never miss a <span className="text-teal glow-text">retail price change</span> in Mauritius.
          </h1>
          <p className="text-base sm:text-lg text-muted mt-5 max-w-xl leading-relaxed">
            PFU Tracker scans the official Price Fixing Unit catalogue every morning. Track your essential
            goods by EAN and get pinged the second a maximum retail price shifts — push on iPhone & Android, plus email.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link to="/signup" className="btn-primary text-base px-6 min-h-[52px]" data-testid="hero-signup-btn">
              Start 30-day free trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" className="btn-ghost text-base px-6 min-h-[52px]" data-testid="hero-login-btn">
              Log in
            </Link>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-6 text-sm text-muted">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald" /> 30 days free, no card</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald" /> Up to 5 profiles</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald" /> Instant PWA push</span>
          </div>
        </div>
        <div className="lg:col-span-5">
          <AlertMock />
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-6xl mx-auto px-4 lg:px-8 py-14 lg:py-20">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-teal mb-3">
          <ArrowDown className="w-4 h-4" /> How it works
        </div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-ink mb-10">
          Three steps. Then leave it running.
        </h2>
        <div className="grid md:grid-cols-3 gap-5">
          {STEPS.map((s) => (
            <div key={s.n} className="card p-6" data-testid={`step-${s.n}`}>
              <div className="font-display text-4xl font-extrabold text-teal/30 mb-3">{s.n}</div>
              <h3 className="font-display text-xl font-semibold text-ink mb-2">{s.title}</h3>
              <p className="text-sm text-muted leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-4 lg:px-8 py-14 lg:py-20">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-ink mb-2">
          Everything you need. Nothing you don't.
        </h2>
        <p className="text-muted mb-10 max-w-2xl">Only what helps you stay ahead of regulated retail price changes.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="card p-6 transition-transform hover:-translate-y-0.5" data-testid={`feature-${f.title.slice(0, 10)}`}>
              <div className="w-10 h-10 rounded-card bg-teal-light flex items-center justify-center mb-4 glow-badge">
                <f.icon className="w-5 h-5 text-teal" />
              </div>
              <h3 className="font-display text-lg font-semibold text-ink mb-1.5">{f.title}</h3>
              <p className="text-sm text-muted leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing — single plan */}
      <section id="pricing" className="max-w-6xl mx-auto px-4 lg:px-8 py-14 lg:py-20">
        <div className="text-center mb-10">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-ink mb-2">Simple, transparent pricing</h2>
          <p className="text-muted">Start with a 30-day full-access free trial. No credit card required.</p>
        </div>
        <div className="max-w-md mx-auto">
          <div className="card glow-border p-8 rise-in" data-testid="pricing-pro-plan-card">
            <span className="chip bg-teal-light text-teal font-mono text-[11px] uppercase tracking-wider mb-4">One plan · all included</span>
            <div className="flex items-end gap-2 mb-1">
              <span className="font-display text-5xl font-extrabold text-ink glow-text">{pricing.display}</span>
              <span className="text-muted mb-2">/{pricing.period}</span>
            </div>
            <p className="text-sm text-muted mb-6">Billed in {pricing.currency} via PayPal · 30-day free trial on signup</p>
            <ul className="space-y-2.5 mb-8">
              {PLAN_FEATURES.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-ink">
                  <CheckCircle2 className="w-4 h-4 text-emerald mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
            <Link to="/signup" className="btn-primary w-full min-h-[52px] text-base" data-testid="pricing-signup-btn">
              Start 30-day free trial <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 pb-16 lg:pb-24">
        <div className="rounded-card p-8 lg:p-12 text-center glow-border bg-teal-light">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink mb-3">
            Ready to track price changes automatically?
          </h2>
          <p className="text-muted max-w-xl mx-auto mb-6">
            PFU Tracker watches the official Mauritius retail and subsidised catalogues so you don't have to.
          </p>
          <Link to="/signup" className="btn-primary text-base px-6 min-h-[52px]" data-testid="cta-signup-btn">
            Create free account <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-border">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 text-sm text-muted" data-testid="footer-powered-by">
            <span>Powered by</span>
            <img src={`${LOGO_WE}?v=2`} alt="WE AI" className="h-7 w-auto" />
            <span>© 2026 PFU Tracker</span>
          </div>
          <div className="flex items-center gap-5 text-sm text-muted">
            <Link to="/login" className="hover:text-ink transition-colors" data-testid="footer-login">Log in</Link>
            <Link to="/signup" className="hover:text-ink transition-colors" data-testid="footer-signup">Sign up</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
